/**
 * A live break's stage cues (boxed's `liveBreak.*` sounds and the host's big-hit cue), synthesized
 * with the Web Audio API on a bus of their own on the page's one sound engine (`openers/kit/sfx.ts`,
 * the engine Retail Box and the neon openers synthesize through): no audio file ships with them.
 * Each cue has a stable id, so a recorded set can replace any of them later (`useRecorded`), one
 * file per id, played through `SoundPlayer` at the cue's own level.
 *
 * The cues, by when a stage plays them:
 * - the Presentation: the announcement band slides in (a bright sweep), its text slides in (a
 *   glittering swoosh), and the box lands (a low thud with a click on top);
 * - each result card as it glides into its buyer: a bling, a bell chime under it and a coin clink,
 *   with one to three more coins dropping after it the more the card is worth against the box;
 * - the host's big hit: a shimmering burst when an epic or legendary pull comes in.
 *
 * Levels follow boxed's mix (the band, text, land, coin and big hit at half, the bling at a fifth,
 * the chime at 0.15), into a gentle glue compressor and a short room. Every failure path is silent,
 * and nothing plays while the page is muted (`setMuted`, which mutes every bus on the page).
 */
import { midi } from "../openers/kit/midi";
import { sfx } from "../openers/kit/sfx";
import { SoundPlayer } from "./SoundPlayer";

/** Every cue a live break's stage plays, by the id a recorded file replaces it under. */
export const LIVE_BREAK_SOUND_IDS = [
  "liveBreak.announcement.band",
  "liveBreak.announcement.text",
  "liveBreak.announcement.land",
  "liveBreak.card.bling",
  "liveBreak.card.chime",
  "liveBreak.card.coin",
  "liveBreak.host.bigHit",
] as const;
export type LiveBreakSoundId = (typeof LIVE_BREAK_SOUND_IDS)[number];

/** A recorded file per cue (an absolute URL); a cue without one keeps its synthesized voice. */
export type LiveBreakSoundManifest = Partial<Record<LiveBreakSoundId, string>>;

/** Each cue's level, 0–1: boxed's multipliers, which a recorded file plays at too. */
export const LIVE_BREAK_SOUND_LEVELS: Record<LiveBreakSoundId, number> = {
  "liveBreak.announcement.band": 0.5,
  "liveBreak.announcement.text": 0.5,
  "liveBreak.announcement.land": 0.5,
  "liveBreak.card.bling": 0.2,
  "liveBreak.card.chime": 0.15,
  "liveBreak.card.coin": 0.5,
  "liveBreak.host.bigHit": 0.5,
};

/** How long after the card's first coin each extra coin drops, and how far either side it may land. */
export const LIVE_BREAK_COIN_DROP_SPACING_MS = 150;
export const LIVE_BREAK_COIN_DROP_JITTER_MS = 60;

/**
 * How many coins drop after a result card's first, from what the card is worth against one box's
 * live-break price (boxed's gem-burst count, `clamp(round(6 + 4·log2(1 + value/price)), 6, 26)`,
 * stepped every seven): 1 for an ordinary pull, 2 from about twice the price, 3 from about nine
 * times it.
 */
export function liveBreakExtraCoinDrops(valueCents: number, priceCents: number): number {
  const ratio = priceCents > 0 ? Math.max(0, valueCents) / priceCents : 0;
  const burst = Math.min(26, Math.max(6, Math.round(6 + 4 * Math.log2(1 + ratio))));
  return Math.min(3, 1 + Math.floor((burst - 6) / 7));
}

/** The cues' studio: a little room, a soft-knee compressor, no saturation (they are bright). */
const bus = sfx.bus({
  level: 0.9,
  compressor: { threshold: -14, knee: 6, ratio: 3, attack: 0.003, release: 0.2 },
  reverb: { seconds: 1.6, level: 0.4 },
});

const rnd = (a: number, b: number) => a + Math.random() * (b - a);

/** A bell: a fundamental and its inharmonic partials, each ringing out a little sooner. */
function bell(freq: number, vol: number, decay: number, when = 0, wet = 0.3) {
  const partials: readonly [number, number, number][] = [
    [1, 1, 1],
    [2.76, 0.45, 0.6],
    [5.4, 0.22, 0.35],
    [8.93, 0.1, 0.2],
  ];
  for (const [ratio, level, length] of partials) {
    bus.tone({ freq: freq * ratio, decay: decay * length, vol: vol * level, when, wet });
  }
}

/** Each cue's synthesized voice, at its level; `vol` scales the whole cue. */
const VOICES: Record<LiveBreakSoundId, (vol: number) => void> = {
  // A bright sweep that peaks a fifth of a second in and darkens as it fades.
  "liveBreak.announcement.band": (vol) => {
    bus.noise({
      freq: 6200,
      to: 1800,
      glide: 1.1,
      q: 0.9,
      attack: 0.18,
      decay: 1.25,
      vol: vol * 0.55,
      wet: 0.35,
    });
    bus.noise({ type: "highpass", freq: 7000, attack: 0.12, decay: 0.35, vol: vol * 0.18 });
    bus.tone({
      type: "sawtooth",
      freq: 220,
      to: 660,
      glide: 0.25,
      attack: 0.15,
      decay: 0.5,
      vol: vol * 0.05,
      wet: 0.3,
    });
  },
  // A busier, glittering swoosh: ticks that thicken toward its middle over a rising sweep.
  "liveBreak.announcement.text": (vol) => {
    bus.noise({
      freq: 1800,
      to: 6500,
      glide: 0.5,
      q: 1.4,
      attack: 0.45,
      decay: 0.55,
      vol: vol * 0.4,
      wet: 0.25,
    });
    for (let i = 0; i < 9; i++) {
      bus.noise({
        type: "highpass",
        freq: rnd(4500, 8500),
        decay: rnd(0.012, 0.03),
        vol: vol * rnd(0.12, 0.28) * (0.5 + i / 12),
        when: 0.06 + i * 0.06 + rnd(-0.015, 0.015),
        pan: rnd(-0.6, 0.6),
      });
    }
    for (const [step, when] of [
      [0, 0.38],
      [4, 0.46],
      [7, 0.55],
    ] as const) {
      bus.tone({
        freq: midi(88 + step),
        type: "triangle",
        decay: 0.28,
        vol: vol * 0.08,
        when,
        wet: 0.4,
      });
    }
  },
  // A heavy thud: a falling sine and a darkening low hit under a short click.
  "liveBreak.announcement.land": (vol) => {
    bus.tone({ freq: 150, to: 42, glide: 0.18, decay: 0.75, vol: vol * 0.95, wet: 0.15 });
    bus.noise({
      type: "lowpass",
      freq: 2200,
      to: 260,
      glide: 0.4,
      decay: 0.45,
      vol: vol * 0.5,
      wet: 0.2,
    });
    bus.noise({ type: "highpass", freq: 2400, decay: 0.018, vol: vol * 0.35 });
    bus.tone({ type: "triangle", freq: 320, to: 110, glide: 0.12, decay: 0.16, vol: vol * 0.25 });
  },
  // A bright metallic sparkle with a softer body under it.
  "liveBreak.card.bling": (vol) => {
    for (const [freq, when] of [
      [6400, 0],
      [7900, 0.02],
      [9300, 0.045],
      [7100, 0.08],
    ] as const) {
      bus.tone({
        freq: freq * rnd(0.98, 1.02),
        decay: 0.42,
        vol: vol * 0.35,
        when,
        pan: rnd(-0.4, 0.4),
        wet: 0.35,
      });
    }
    bus.tone({ freq: 2300, type: "triangle", decay: 0.8, vol: vol * 0.4, wet: 0.3 });
    bus.noise({ type: "highpass", freq: 6000, decay: 0.12, vol: vol * 0.2 });
  },
  // A steady bell around 2.5 kHz with a long ring.
  "liveBreak.card.chime": (vol) => {
    bell(2480, vol * 0.9, 2.6, 0, 0.4);
  },
  // A short, bright clink.
  "liveBreak.card.coin": (vol) => {
    const pitch = rnd(0.96, 1.05);
    bus.tone({ freq: 4200 * pitch, decay: 0.16, vol: vol * 0.45, wet: 0.2 });
    bus.tone({ freq: 6900 * pitch, decay: 0.11, vol: vol * 0.3, when: 0.012, wet: 0.2 });
    bus.noise({ type: "highpass", freq: 7500, decay: 0.03, vol: vol * 0.25 });
  },
  // A dense, shimmering burst: a wash that swells and dips, a sparkle run over it, and a late tail.
  "liveBreak.host.bigHit": (vol) => {
    bus.noise({
      type: "highpass",
      freq: 6000,
      to: 9500,
      glide: 1,
      attack: 0.2,
      decay: 0.9,
      vol: vol * 0.3,
      wet: 0.5,
    });
    bus.tone({ freq: 70, to: 38, glide: 0.5, decay: 0.9, vol: vol * 0.5 });
    const chord = [0, 4, 7, 12, 16, 19, 24];
    chord.forEach((step, i) => {
      bus.tone({
        freq: midi(76 + step),
        type: "triangle",
        decay: 0.7,
        vol: vol * 0.12,
        when: 0.08 + i * 0.06,
        wet: 0.45,
      });
    });
    for (let i = 0; i < 10; i++) {
      bus.tone({
        freq: midi(96 + (chord[i % chord.length] ?? 0)),
        decay: 0.3,
        vol: vol * 0.05,
        when: 0.2 + i * 0.07 + rnd(0, 0.03),
        pan: rnd(-0.7, 0.7),
        wet: 0.5,
      });
    }
    bus.noise({
      type: "highpass",
      freq: 8800,
      attack: 0.1,
      decay: 0.5,
      vol: vol * 0.12,
      when: 1.7,
      wet: 0.6,
    });
  },
};

class LiveBreakSounds {
  private recorded: LiveBreakSoundManifest = {};
  private player: SoundPlayer | null = null;
  private timers = new Set<ReturnType<typeof setTimeout>>();

  /** Whether the page's sound is muted (every bus on the page, an opener's included). */
  get muted(): boolean {
    return sfx.isMuted;
  }

  /** Mutes or unmutes the page's sound; a coin still due to drop stays silent while muted. */
  setMuted(muted: boolean): void {
    sfx.setMuted(muted);
  }

  /** Sets the page's volume, 0 to 1 (the shopper's level as a gain), the openers' engine with it. */
  setVolume(gain: number): void {
    sfx.setVolume(gain);
  }

  /** Call from a user gesture: browsers start audio only from one (the engine also listens). */
  unlock(): void {
    sfx.unlock();
    if (Object.keys(this.recorded).length > 0) this.recordedPlayer().unlock();
  }

  /**
   * Replaces cues with recorded files, one per id (absolute URLs, loaded now); an id left out keeps
   * its synthesized voice. Pass `{}` to go back to every synthesized voice.
   */
  useRecorded(manifest: LiveBreakSoundManifest): void {
    this.recorded = { ...manifest };
    const player = this.recordedPlayer();
    for (const url of Object.values(this.recorded)) if (url) player.preload(url);
  }

  /** Plays one cue now, or `delayMs` from now. */
  play(id: LiveBreakSoundId, { delayMs = 0 }: { delayMs?: number } = {}): void {
    if (delayMs > 0) {
      this.later(delayMs, () => this.play(id));
      return;
    }
    if (sfx.isMuted) return;
    const level = LIVE_BREAK_SOUND_LEVELS[id];
    const url = this.recorded[id];
    if (url) {
      this.recordedPlayer().play(url, { volume: level });
      return;
    }
    try {
      VOICES[id](level);
    } catch {
      // Degrade silently, like every sound on the page.
    }
  }

  /**
   * One result card hitting its buyer: the bling, the chime and a coin together, then the extra
   * coins (`liveBreakExtraCoinDrops`) a beat apart, each a little early or late.
   */
  playCardImpact({
    valueCents,
    priceCents,
    delayMs = 0,
  }: {
    valueCents: number;
    priceCents: number;
    delayMs?: number;
  }): void {
    if (delayMs > 0) {
      this.later(delayMs, () => this.playCardImpact({ valueCents, priceCents }));
      return;
    }
    this.play("liveBreak.card.bling");
    this.play("liveBreak.card.chime");
    this.play("liveBreak.card.coin");
    const drops = liveBreakExtraCoinDrops(valueCents, priceCents);
    for (let drop = 1; drop <= drops; drop++) {
      const jitter = (Math.random() * 2 - 1) * LIVE_BREAK_COIN_DROP_JITTER_MS;
      this.play("liveBreak.card.coin", {
        delayMs: Math.max(1, drop * LIVE_BREAK_COIN_DROP_SPACING_MS + jitter),
      });
    }
  }

  /** Drops every cue still waiting to play (the page left, or the stage moved on). */
  cancel(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
  }

  private later(delayMs: number, run: () => void): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      run();
    }, delayMs);
    this.timers.add(timer);
  }

  private recordedPlayer(): SoundPlayer {
    if (!this.player) this.player = new SoundPlayer();
    return this.player;
  }
}

/** The page's live-break cues: one set per page, on the page's one sound engine. */
export const liveBreakSounds = new LiveBreakSounds();
